import React from "react";
import { LegalPageShell } from "./Legal";

const DecorativeBlobs = () => (
  <svg
    width="0"
    height="0"
    style={{ position: "absolute" }}
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <g id="cookies-blob-1" transform="translate(-100,-100)">
        <path d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z" />
        <path d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z" />
      </g>
      <g id="cookies-blob-2" transform="translate(100,-100) scale(-1,1)">
        <path d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z" />
        <path d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z" />
      </g>
    </defs>
  </svg>
);

const HeroPattern = () => (
  <div className="hero-pattern" aria-hidden="true">
    <svg
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <svg x="6%" y="22%" overflow="visible">
        <use
          href="#cookies-blob-1"
          fill="#FFA5BC"
          transform="scale(1.5)"
          opacity="0.16"
        />
      </svg>
      <svg x="84%" y="76%" overflow="visible">
        <use
          href="#cookies-blob-2"
          fill="#B798F2"
          transform="scale(1.8)"
          opacity="0.14"
        />
      </svg>
      <svg x="50%" y="-15%" overflow="visible">
        <use
          href="#cookies-blob-1"
          fill="#E8EBFF"
          transform="scale(1.4)"
          opacity="0.55"
        />
      </svg>
      <svg x="68%" y="58%" overflow="visible">
        <use
          href="#cookies-blob-2"
          fill="#F3EC7A"
          transform="scale(0.85)"
          opacity="0.30"
        />
      </svg>
    </svg>
  </div>
);

const BandedSection = ({
  children,
  id,
  labelledBy,
}: {
  children: React.ReactNode;
  id: string;
  labelledBy: string;
}) => (
  <section
    id={id}
    aria-labelledby={labelledBy}
    style={{
      background: "var(--cream-ink)",
      borderTop: "1px solid var(--grey-200)",
      borderBottom: "1px solid var(--grey-200)",
    }}
  >
    {children}
  </section>
);

const Cookies = () => (
  <LegalPageShell id="cookies">
    <main id="main-content">
      <DecorativeBlobs />

      <header className="hero">
        <HeroPattern />
        <div className="container">
          <span className="eyebrow">Cookie Policy</span>
          <h1 className="hero-title">
            Cookies, <em>the digital kind</em>.
          </h1>
          <p className="hero-sub">
            <strong>
              We use the bare minimum to make the site work, and we do not track
              or retarget you.
            </strong>{" "}
            No analytics following you about, no advertising pixels, and no
            annoying pop-up to click away. Here is the lot.
          </p>
          <div className="hero-meta" aria-label="Page metadata">
            <span>
              <strong>Read time:</strong> about 3 minutes
            </span>
          </div>
        </div>
      </header>

      <section className="tldr-wrap" aria-labelledby="tldr-title">
        <div className="narrow">
          <div className="tldr">
            <div className="label">If you only read one thing</div>
            <h2 id="tldr-title">
              The whole policy, <em>in five bullets</em>.
            </h2>
            <ul>
              <li>
                <strong>We only use essential cookies.</strong> The ones that
                keep you logged in and your payment secure.
              </li>
              <li>
                <strong>We do not track you.</strong> No analytics that identify
                you, no advertising cookies, nothing clocking where you wander
                next.
              </li>
              <li>
                <strong>We never retarget you.</strong> We will not follow you
                round the internet with ads, mainly because we do not run any.
              </li>
              <li>
                <strong>That is why there is no cookie pop-up.</strong> With
                nothing non-essential to consent to, there is nothing to nag you
                about.
              </li>
              <li>
                <strong>Got a question?</strong> Email{" "}
                <a href="mailto:groundcontrol@42courses.com">
                  groundcontrol@42courses.com
                </a>
                . We reply within 24 hours, often sooner.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <nav className="toc" aria-label="Jump to a section">
        <div className="container toc-inner">
          <span className="toc-label">Jump to:</span>
          <div className="toc-pills">
            <a href="#what" className="toc-pill">
              What a cookie is
            </a>
            <a href="#use" className="toc-pill">
              What we use
            </a>
            <a href="#list" className="toc-pill">
              The cookie list
            </a>
            <a href="#dont" className="toc-pill">
              What we do not do
            </a>
            <a href="#manage" className="toc-pill">
              Managing them
            </a>
            <a href="#third" className="toc-pill">
              Third parties
            </a>
            <a href="#changes" className="toc-pill">
              Changes
            </a>
            <a href="#contact" className="toc-pill">
              Contact
            </a>
          </div>
        </div>
      </nav>

      <section id="what" aria-labelledby="what-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">01</span>
            <h2 className="section-title" id="what-title">
              What a cookie <em>actually is</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              A cookie is a small text file a website leaves on your device so
              it can remember things, like that you are logged in, or that you
              prefer the site in French.
            </div>
          </div>
          <div className="prose">
            <p>
              Cookies do useful, dull work. They keep you signed in as you move
              between pages, hold the course in your basket while you reach for
              your card, and remember the language you picked last time.
            </p>
            <p>
              This policy sits alongside our{" "}
              <a href="/privacy">Privacy Policy</a>, which explains what we do
              with personal data more broadly.
            </p>
          </div>
        </div>
      </section>

      <BandedSection id="use" labelledBy="use-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">02</span>
            <h2 className="section-title" id="use-title">
              What we <em>use, and why it is so short</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              Two kinds, both boring, both about making the site work. Nothing
              that watches you.
            </div>
          </div>
          <div className="prose">
            <h3>Strictly necessary</h3>
            <p>
              These keep you logged in, remember your basket, and protect
              against fraud at checkout. They cannot be switched off without
              breaking the site, so under UK cookie law (PECR) we are allowed to
              use them without asking.
            </p>
            <h3>Functional</h3>
            <p>
              A couple of preference cookies remember how you like the site:
              your language, your last lesson, that sort of thing. They make
              life smoother, hold nothing sensitive, and you can clear them
              whenever you like.
            </p>
            <p>
              And that is the entire list of categories.{" "}
              <strong>
                No analytics cookies that identify you. No advertising or
                retargeting cookies. None.
              </strong>
            </p>
          </div>
        </div>
      </BandedSection>

      <section id="list" aria-labelledby="list-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">03</span>
            <h2 className="section-title" id="list-title">
              The cookie <em>list</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              Every cookie you will meet on 42courses, in one short table. If we
              have missed one, tell us and we will add it.
            </div>
          </div>
          <div
            className="data-table"
            role="table"
            aria-label="Cookies used by 42courses"
          >
            <div className="data-row" role="row">
              <div role="columnheader">Cookie</div>
              <div role="columnheader">What it does</div>
              <div role="columnheader">Lasts</div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Cookie">
                <strong>_42courses_session</strong>
              </div>
              <div data-label="What it does">
                Strictly necessary. Keeps you logged in and your session secure.
              </div>
              <div data-label="Lasts">Session</div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Cookie">
                <strong>CSRF token</strong>
              </div>
              <div data-label="What it does">
                Strictly necessary. Protects forms against forged requests.
              </div>
              <div data-label="Lasts">Session</div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Cookie">
                <strong>__stripe_mid / __stripe_sid</strong>
              </div>
              <div data-label="What it does">
                Strictly necessary. Set by Stripe to prevent fraud during
                payment.
              </div>
              <div data-label="Lasts">1 year / 30 mins</div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Cookie">
                <strong>prefs (language, theme, last lesson)</strong>
              </div>
              <div data-label="What it does">
                Functional. Remembers how you like the site set up.
              </div>
              <div data-label="Lasts">12 months</div>
            </div>
          </div>
        </div>
      </section>

      <BandedSection id="dont" labelledBy="dont-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">04</span>
            <h2 className="section-title" id="dont-title">
              What we <em>deliberately do not do</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              The list of things we have chosen not to do is longer than the
              list of cookies we use. We are rather proud of that.
            </div>
          </div>
          <div className="prose">
            <ul>
              <li>
                <strong>
                  We do not run advertising or retargeting cookies.
                </strong>{" "}
                You will never see one of our ads stalking you across the web,
                because there are none to serve.
              </li>
              <li>
                <strong>
                  We do not use analytics cookies that identify you.
                </strong>{" "}
                We are not building a profile of your every click.
              </li>
              <li>
                <strong>We do not sell your data.</strong> Partly because we
                would not, partly because there is nothing to sell.
              </li>
              <li>
                <strong>
                  We do not share cookie data with advertisers or data brokers.
                </strong>{" "}
                They are not getting an invite.
              </li>
            </ul>
            <p>
              It is a deliberate choice, not an oversight. Good learning should
              not come with a side of surveillance.
            </p>
          </div>
        </div>
      </BandedSection>

      <section id="manage" aria-labelledby="manage-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">05</span>
            <h2 className="section-title" id="manage-title">
              Managing <em>your cookies</em>.
            </h2>
          </div>
          <div className="prose">
            <p>
              Since everything we set is essential or a simple preference, there
              is nothing to opt out of. If you would still like to clear or
              block cookies, your browser is the place to do it: Chrome, Safari,
              Firefox and Edge all have help pages that walk you through it.
            </p>
            <p>
              One thing to know: block the strictly necessary cookies and parts
              of the site, like logging in and checkout, will simply stop
              working.
            </p>
          </div>
        </div>
      </section>

      <BandedSection id="third" labelledBy="third-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">06</span>
            <h2 className="section-title" id="third-title">
              Third-party <em>cookies</em>.
            </h2>
          </div>
          <div className="prose">
            <p>
              We keep outside help to a minimum. The only third parties that may
              set a cookie when you use 42courses are the ones that make core
              features work:
            </p>
            <ul>
              <li>
                <strong>Stripe</strong> handles payments and sets
                fraud-prevention cookies at checkout.
              </li>
              <li>
                <strong>Embedded video</strong> may set a cookie from the video
                host once you press play.
              </li>
            </ul>
            <p>
              That is the lot. No advertising networks, no social media
              trackers, no data brokers. More on suppliers lives in our{" "}
              <a href="/privacy">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </BandedSection>

      <section id="changes" aria-labelledby="changes-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">07</span>
            <h2 className="section-title" id="changes-title">
              When this <em>changes</em>.
            </h2>
          </div>
          <div className="prose">
            <p>
              If we ever decide to add anything non-essential, analytics or
              otherwise, we will ask for your consent first, and we will update
              this page before it happens. Until you see that, you can take this
              page at face value: essentials only.
            </p>
          </div>
        </div>
      </section>

      <section id="contact" aria-labelledby="contact-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">08</span>
            <h2 className="section-title" id="contact-title">
              Contacting <em>us</em>.
            </h2>
          </div>
          <div className="contact-box">
            <h3>
              One inbox, <em>one human reply</em>.
            </h3>
            <p>
              Questions about cookies, tracking, or anything on this page: email{" "}
              <a href="mailto:groundcontrol@42courses.com">
                groundcontrol@42courses.com
              </a>{" "}
              and we will point it at the right person.
            </p>
            <p>
              For data-protection complaints you can also contact the UK
              Information Commissioner's Office at{" "}
              <a
                href="https://ico.org.uk/make-a-complaint"
                target="_blank"
                rel="noopener noreferrer"
              >
                ico.org.uk/make-a-complaint
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="still-got-q" aria-labelledby="still-q-title">
        <div className="container">
          <h2 id="still-q-title">
            Still got a <em>question</em>?
          </h2>
          <p>
            If it is not on this page, email a real human. We reply within 24
            hours, often sooner.
          </p>
          <div className="btn-row">
            <a
              href="mailto:groundcontrol@42courses.com"
              className="btn btn-primary"
            >
              Email Ground Control
            </a>
            <a href="/privacy" className="btn btn-secondary">
              Read the Privacy Policy
            </a>
          </div>
        </div>
      </section>

      <footer className="footer-egg">
        <div className="big">
          <em>Essential only.</em>
        </div>
        <div className="small">No tracking. No retargeting. No pop-up.</div>
        <div className="version">Version: 13 June 2026</div>
      </footer>
    </main>
  </LegalPageShell>
);

export default Cookies;
